import { environment } from '@env/environment';

/**
 * Construye la URL completa basándose en el ambiente y la ruta proporcionada
 * @param currentRoute Ruta actual del router
 * @param excludeParams Parámetros a excluir de la URL (opcional)
 * @returns URL completa de la pantalla
 */
export function buildFullUrl(currentRoute: string, excludeParams?: string[]): string {
  const baseUrl = environment.production
    ? 'https://ecomm.sura.co'
    : 'https://arriendolab.ecomm.sura.co';

  // Si no hay parámetros a excluir, retornar la URL completa
  if (!excludeParams || excludeParams.length === 0) {
    return `${baseUrl}${currentRoute}`;
  }

  // Separar la ruta de los query params
  const [path, queryString] = currentRoute.split('?');

  // Si no hay query string, retornar la URL sin cambios
  if (!queryString) {
    return `${baseUrl}${path}`;
  }

  // Parsear los query params
  const urlSearchParams = new URLSearchParams(queryString);

  // Eliminar los parámetros especificados
  excludeParams.forEach(param => {
    urlSearchParams.delete(param);
  });

  // Reconstruir la URL
  const cleanQueryString = urlSearchParams.toString();
  const finalRoute = cleanQueryString ? `${path}?${cleanQueryString}` : path;

  return `${baseUrl}${finalRoute}`;
}

/**
 * Detecta si el ambiente actual es producción basándose en la URL del navegador
 * @returns true si es producción, false si no lo es
 */
export function isProductionEnvironment(): boolean {
  const currentUrl = window.location.href;
  return currentUrl.includes('ecomm.sura.co') && !currentUrl.includes('arriendolab');
}

/**
 * Construye la URL completa detectando el ambiente desde window.location
 * @param currentRoute Ruta actual del router
 * @param excludeParams Parámetros a excluir de la URL (opcional)
 * @returns URL completa de la pantalla
 */
export function buildFullUrlFromWindow(currentRoute: string, excludeParams?: string[]): string {
  const baseUrl = isProductionEnvironment()
    ? 'https://ecomm.sura.co'
    : 'https://arriendolab.ecomm.sura.co';

  // Si no hay parámetros a excluir, retornar la URL completa
  if (!excludeParams || excludeParams.length === 0) {
    return `${baseUrl}${currentRoute}`;
  }

  // Separar la ruta de los query params
  const [path, queryString] = currentRoute.split('?');

  // Si no hay query string, retornar la URL sin cambios
  if (!queryString) {
    return `${baseUrl}${path}`;
  }

  // Parsear los query params
  const urlSearchParams = new URLSearchParams(queryString);

  // Eliminar los parámetros especificados
  excludeParams.forEach(param => {
    urlSearchParams.delete(param);
  });

  // Reconstruir la URL
  const cleanQueryString = urlSearchParams.toString();
  const finalRoute = cleanQueryString ? `${path}?${cleanQueryString}` : path;

  return `${baseUrl}${finalRoute}`;
}

/**
 * Prepara el body del orquestador con los campos de inactividad
 * @param body Body base generado por GeneralDataService
 * @param options Opciones adicionales para el body
 * @returns Body completo con campos de inactividad
 */
export function buildInactivityBody(
  body: any,
  options: {
    estadoRescate: 'Habilitado' | 'Inhabilitado';
    estadoRetoma?: 'Habilitado' | 'Inhabilitado';
    url: string;
    pantalla: string;
    leadRequirement?: string;
    optionalProducts?: object;
    getLeadDuplicated?: boolean;
  }
): any {
  const result: any = {
    ...body,
    optionalProducts: options.optionalProducts || {},
    estadoRescate: options.estadoRescate,
    UrlRetoma: options.url,
    pantalla: options.pantalla,
    leadRequirement: options.leadRequirement || body.leadRequirement,
    getLeadDuplicated: options.getLeadDuplicated !== undefined ? options.getLeadDuplicated : body.getLeadDuplicated || false
  };

  // Solo agregar estadoRetoma si está presente en las opciones
  if (options.estadoRetoma !== undefined) {
    result.estadoRetoma = options.estadoRetoma;
  }

  return result;
}

/**
 * Prepara las opciones para generar el body del orquestador
 * @param keys Keys para incluir en el body
 * @param leadRequirement Requerimiento del lead
 * @param asesorCode Código del asesor (opcional)
 * @param bitacoraId ID de bitácora (opcional)
 * @param screenPercentage Porcentaje de pantalla (opcional)
 * @param optionalProducts Productos opcionales (opcional)
 * @param getLeadDuplicated Indica si se debe validar lead duplicado (opcional)
 * @returns Objeto de opciones para GeneralDataService.getbody()
 */
export function buildOrchestatorOptions(
  keys: string[],
  leadRequirement: string,
  asesorCode?: string,
  bitacoraId?: string,
  screenPercentage?: string,
  optionalProducts?: object,
  getLeadDuplicated?: boolean
): any {
  return {
    keys: keys,
    screenPercentage: screenPercentage || '',
    leadRequirement: leadRequirement || '',
    leadId: 'leadId',
    asesorCode: asesorCode !== '' ? asesorCode : undefined,
    bitacoraId: bitacoraId !== '' ? bitacoraId : undefined,
    optionalProducts: optionalProducts || {},
    getLeadDuplicated: getLeadDuplicated || false
  };
}
